import React, {
  useEffect, useMemo, useState, useCallback, useRef,
} from 'react'
import PropTypes from 'prop-types'
import ReactMarkdown from 'react-markdown'
import classnames from 'classnames'
import moment from 'moment'
import TableInput from 'components/Form/components/TableInput'
import SelectRadioSwitchInput from 'components/Form/components/SelectRadioSwitchInput'
import ExternalLinkRenderer from 'components/ExternalLinkRenderer'
import {
  ToggleInput, TextField, DatePicker,
} from '@deloitte/gel-library'
import { getConfig } from 'utils/config'
import { isNullOrWhiteSpace } from 'utils/strings'
import styles from './Parameter.scss'

const propTypes = {
  className: PropTypes.string,
  dataTestId: PropTypes.string,
  disabled: PropTypes.bool,
  field: PropTypes.object.isRequired,
  formData: PropTypes.shape({
    error: PropTypes.string,
    formKey: PropTypes.string,
    valid: PropTypes.bool,
    value: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.bool,
      PropTypes.array,
    ]),
  }).isRequired,
  index: PropTypes.number,
  onChange: PropTypes.func.isRequired,
  onFocus: PropTypes.func,
  onValidation: PropTypes.func.isRequired,
  simple: PropTypes.bool,
}

const defaultProps = {
  className: null,
  dataTestId: '',
  disabled: false,
  index: null,
  onFocus: () => {},
  simple: false,
}

function Parameter({
  className,
  dataTestId, disabled, field,
  formData, index, onValidation,
  onChange, onFocus, simple,
}) {
  const [localValue, setLocalValue] = useState(formData.value)
  const [isFocused, setIsFocused] = useState(false)

  const onChangeRef = useRef(onChange)
  const onValidationRef = useRef(onValidation)
  const onFocusRef = useRef(onFocus)

  onChangeRef.current = onChange
  onValidationRef.current = onValidation
  onFocusRef.current = onFocus

  useEffect(() => {
    if (!isFocused && formData.value !== localValue) {
      setLocalValue(formData.value)
    }
  }, [formData.value, isFocused, localValue])

  const { description, isDynamic } = useMemo(() => {
    if (!field.description) {
      return { description: null, isDynamic: false }
    }

    const hasDynamicContent = field.description.indexOf('|VALUE|') > -1
    const processedDescription = hasDynamicContent
      ? field.description.replace(/\|VALUE\|/g, localValue)
      : field.description

    return { description: processedDescription, isDynamic: hasDynamicContent }
  }, [field.description, localValue])

  const supportSiteLinkMarkdown = useMemo(() => {
    return field.supportArticleId
      ? ` [Learn more](${getConfig().URL.SUPPORT}/${field.supportArticleId} "Further information about this parameter.")`
      : ''
  }, [field.supportArticleId])

  const handleTextChange = useCallback((fieldKey, value) => {
    setLocalValue(value)
  }, [])

  const handleTextBlur = useCallback(() => {
    setIsFocused(false)
    const currentValue = localValue
    onChangeRef.current(field.formKey, currentValue)
    onValidationRef.current(field.formKey, currentValue)
  }, [field.formKey, localValue])

  const handleTextFocus = useCallback(() => {
    setIsFocused(true)
    if (onFocusRef.current) {
      onFocusRef.current(field.formKey)
    }
  }, [field.formKey])

  const handleNonTextChange = useCallback((fieldKey, value) => {
    setLocalValue(value)
    setIsFocused(false)
    onChangeRef.current(fieldKey, value)
  }, [])

  const handleNonTextBlur = useCallback((fieldKey, value, context) => {
    setIsFocused(false)
    onValidationRef.current(fieldKey, value, context)
  }, [])

  const handleToggleChange = useCallback((fieldKey, value) => {
    setLocalValue(value)
    setTimeout(() => {
      onChangeRef.current(fieldKey, value)
    }, 150)
  }, [])

  const handleImmediateChange = useCallback((fieldKey, value) => {
    setLocalValue(value)
    onChangeRef.current(fieldKey, value)
  }, [])

  const { Element, ElementType } = useMemo(() => {
    let elementComponent
    let elementType

    switch (field.type) {
      case 'BIT':
      case 'boolean':
        elementType = 'ToggleInput'
        elementComponent = (
          <div data-test-id={`toggleInput-${dataTestId}`}>
            <ToggleInput
              checked={localValue === 1 || localValue === 'true' || localValue === true}
              className={className}
              data-test-id={`toggleInput-${dataTestId}`}
              disabled={disabled}
              label=""
              name={field.formKey}
              onChange={handleToggleChange}
            />
          </div>
        )
        break
      case 'COMBO BOX':
      case 'dropdown':
        elementType = 'SelectRadioSwitchInput'
        elementComponent = (
          <SelectRadioSwitchInput
            floatedLabel
            className={styles.inputfieldSelectInput}
            data-test-id={`selectRadioSwitchInput-${dataTestId}`}
            disabled={disabled}
            error={formData.error}
            label=""
            name={field.formKey}
            onBlur={() => handleNonTextBlur(field.formKey, localValue)}
            onChange={handleNonTextChange}
            options={field.options}
            searchable={false}
            value={localValue}
          />
        )
        break
      case 'DATE':
      case 'date':
        elementType = 'DateInput'
        elementComponent = (
          <DatePicker
            className={className}
            data-test-id={`dateInput-${dataTestId}`}
            disabled={disabled}
            error={!isNullOrWhiteSpace(formData.error)}
            fullWidth={false}
            helperText={formData.error}
            label=""
            name={field.formKey}
            onBlur={() => handleNonTextBlur(field.formKey, localValue)}
            onChange={(date) => {
              handleImmediateChange(field.formKey, moment(date).format('YYYY-MM-DD'))
            }}
            value={localValue === '' ? null : localValue}
          />
        )
        break
      case 'TABLE':
        elementType = 'TableInput'
        elementComponent = (
          <TableInput
            className={className}
            data-test-id={`tableInput-${dataTestId}`}
            disabled={disabled}
            error={formData.error}
            label=""
            name={field.formKey}
            onBlur={context => handleNonTextBlur(field.formKey, localValue, context)}
            onChange={handleNonTextChange}
            {...(onFocusRef.current && { onFocus: name => onFocusRef.current(name) })}
            schema={field.schema}
            value={localValue}
          />
        )
        break
      case 'FLOAT':
      case 'float':
      case 'INT':
      case 'int':
      case 'BIGINT':
      case 'bigint':
        elementType = 'NumberInput'
        elementComponent = (
          <TextField
            fullWidth
            className={className}
            data-test-id={`numberInput-${dataTestId}`}
            disabled={disabled}
            error={formData.error}
            label=""
            name={field.formKey}
            onBlur={handleTextBlur}
            onChange={handleTextChange}
            onFocus={handleTextFocus}
            type="text"
            value={`${localValue}`}
          />
        )
        break
      case 'MONEY':
      case 'money':
        elementType = 'NumberInput'
        elementComponent = (
          <TextField
            fullWidth
            adornment="$"
            className={className}
            data-test-id={`numberInput-${dataTestId}`}
            disabled={disabled}
            error={formData.error}
            label=""
            name={field.formKey}
            onBlur={handleTextBlur}
            onChange={handleTextChange}
            onFocus={handleTextFocus}
            type="text"
            value={`${localValue}`}
          />
        )
        break
      default:
        elementType = 'TextInput'
        elementComponent = (
          <TextField
            fullWidth
            multiline
            className={className}
            data-test-id={`textInput-${dataTestId}`}
            disabled={disabled}
            error={formData.error}
            label=""
            name={field.formKey}
            onBlur={handleTextBlur}
            onChange={handleTextChange}
            onFocus={handleTextFocus}
            type="text"
            value={localValue}
          />
        )
    }

    return { Element: elementComponent, ElementType: elementType }
  }, [
    field.type,
    field.formKey,
    field.options,
    field.schema,
    formData.error,
    className,
    dataTestId,
    disabled,
    handleTextChange,
    handleTextBlur,
    handleTextFocus,
    handleNonTextChange,
    handleNonTextBlur,
    handleToggleChange,
    handleImmediateChange,
    localValue,
    onFocusRef,
  ])

  const Description = useMemo(() => {
    if (!description) { return null }

    return (
      <div className={classnames(styles.staticDescription, { [styles.dynamicDescription]: isDynamic })}>
        <ReactMarkdown
          renderers={{ link: ExternalLinkRenderer }}
          source={`${description}${supportSiteLinkMarkdown}`}
        />
      </div>
    )
  }, [description, isDynamic, supportSiteLinkMarkdown])

  if (simple) {
    return (
      <div className={classnames(styles.inputfield, styles.simple)}>
        {Element}
        {Description}
      </div>
    )
  }

  return (
    <div>
      <div
        className={classnames(
          'row',
          styles.inputfield,
          styles[`inputfield${ElementType}`],
        )}
        key={index}
        style={{ marginLeft: 0, marginRight: 0, marginBottom: '34px' }}
      >
        <div className="col-xs-12 col-sm-4 col-md-4">
          <p>{field.name}</p>
        </div>
        <div className="col-xs-12 col-sm-8 col-md-8">
          <div>{Element}</div>
          {field.description && Description}
        </div>
      </div>
    </div>
  )
}

Parameter.propTypes = propTypes
Parameter.defaultProps = defaultProps

export default Parameter
